<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import BillingPlanAPI from 'dashboard/api/billingPlan';
import NextButton from 'dashboard/components-next/button/Button.vue';
import PlanComparisonTable from 'dashboard/components-next/Billing/PlanComparisonTable.vue';

const { t } = useI18n();

const isLoading = ref(true);
const isSubscribing = ref(false);
const isSyncing = ref(false);
const currentPlanId = ref('free');
const products = ref([]);
const features = ref([]);
const billingDevMode = ref(false);

const paidProducts = () =>
  products.value.filter(product => product.id !== 'free');

onMounted(async () => {
  try {
    const { data } = await BillingPlanAPI.get();
    currentPlanId.value = data.current_plan_id;
    products.value = data.products.filter(product =>
      ['free', 'pro', 'plus_ia'].includes(product.id)
    );
    features.value = data.features;
    billingDevMode.value = data.billing_dev_mode;
  } catch (error) {
    // Ignore error, comparison is optional at this step
  } finally {
    isLoading.value = false;
  }
});

const openCheckout = product => {
  window.open(product.purchase_url, '_blank', 'noopener,noreferrer');
};

const subscribeDevMode = async product => {
  isSubscribing.value = true;
  try {
    const { data } = await BillingPlanAPI.update(product.id);
    currentPlanId.value = data.current_plan_id;
    useAlert(t('QUICK_SETUP.STEP_PLAN.SUBSCRIBED'));
  } catch (error) {
    useAlert(t('QUICK_SETUP.STEP_PLAN.SUBSCRIBE_ERROR'));
  } finally {
    isSubscribing.value = false;
  }
};

const sync = async () => {
  isSyncing.value = true;
  try {
    const { data } = await BillingPlanAPI.sync();
    currentPlanId.value = data.current_plan_id;
    useAlert(t('QUICK_SETUP.STEP_PLAN.SUBSCRIBED'));
  } catch (error) {
    useAlert(t('QUICK_SETUP.STEP_PLAN.SYNC_ERROR'));
  } finally {
    isSyncing.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col gap-5">
    <p class="text-sm text-n-slate-11">
      {{ t('QUICK_SETUP.STEP_PLAN.DESCRIPTION') }}
    </p>

    <PlanComparisonTable
      v-if="!isLoading"
      :products="products"
      :features="features"
      :current-plan-id="currentPlanId"
    />

    <div
      v-for="product in paidProducts()"
      :key="product.id"
      class="flex items-center gap-2"
    >
      <template v-if="currentPlanId === product.id">
        <p class="text-sm text-n-teal-11">
          {{ t('QUICK_SETUP.STEP_PLAN.SUBSCRIBED') }}
        </p>
      </template>
      <template v-else-if="product.purchase_url">
        <NextButton
          color="blue"
          type="button"
          :label="
            t('QUICK_SETUP.STEP_PLAN.SUBSCRIBE_TO', { plan: product.name })
          "
          @click="openCheckout(product)"
        />
        <NextButton
          color="slate"
          variant="faded"
          type="button"
          :is-loading="isSyncing"
          :label="t('QUICK_SETUP.STEP_PLAN.ALREADY_SUBSCRIBED')"
          @click="sync"
        />
      </template>
      <template v-else-if="billingDevMode">
        <NextButton
          color="blue"
          type="button"
          :is-loading="isSubscribing"
          :label="
            t('QUICK_SETUP.STEP_PLAN.SUBSCRIBE_TO', { plan: product.name })
          "
          @click="subscribeDevMode(product)"
        />
      </template>
      <template v-else>
        <NextButton
          color="slate"
          variant="faded"
          type="button"
          disabled
          :label="t('QUICK_SETUP.STEP_PLAN.COMING_SOON')"
        />
      </template>
    </div>
  </div>
</template>
